/**
 * Color Conversion Utilities
 * 
 * Converts color names to hex values for mask zone extraction
 */

/**
 * Color name to hex mapping
 * Colors should be manually added when new zones are added
 */
const COLOR_NAME_TO_HEX: Record<string, string> = {
  Red: '#FF0000',
  Green: '#00FF00',
  Blue: '#0000FF',
  Yellow: '#FFFF00',
  Cyan: '#00FFFF',
  Magenta: '#FF00FF',
  Orange: '#FFA500',
  Purple: '#800080',
  Pink: '#FFC0CB',
  Black: '#000000',
  White: '#FFFFFF',
  Gray: '#808080',
  Grey: '#808080'
};

/**
 * Convert color name to hex value
 * @param colorName - Color name (e.g., "Red", "Blue") or hex value (e.g., "#FF0000")
 * @returns Hex color value (e.g., "#FF0000")
 */
export function colorNameToHex(colorName: string): string {
  // If already a hex value, return as-is
  if (colorName.startsWith('#')) {
    return colorName.toUpperCase();
  }

  // Look up color name in mapping
  const hex = COLOR_NAME_TO_HEX[colorName];
  if (hex) {
    return hex;
  }

  // If not found, log warning and return a default
  console.warn(`Color name "${colorName}" not found in mapping. Using default "#FF0000"`);
  return '#FF0000';
}

/**
 * Parse hex color to RGB values
 * @param hex - Hex color value (e.g., "#FF0000")
 * @returns RGB values { r: number, g: number, b: number }
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalised = hex.replace('#', '').trim();
  const expanded =
    normalised.length === 3
      ? normalised
          .split('')
          .map((char) => char + char)
          .join('')
      : normalised;

  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expanded);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : { r: 255, g: 0, b: 0 }; // Default to red if parsing fails
}

